import { supabase } from '../clients/supabaseClient';
import type { Special, SpecialSchedule, ProductItem, CategoryItem, StorageImageItem } from '../types';


const SEARCH_RESULT_LIMIT = 20;
export const IMAGE_PAGE_SIZE = 24;
export const SEARCH_DEBOUNCE_MS = 250;
export const MIN_SEARCH_CHARS = 2;
export const BUSINESS_TIME_ZONE = 'Africa/Johannesburg';

// Fetches all specials and their schedules in parallel.
// Both are returned together since schedules are always needed alongside specials
// to determine visibility rules and display timing.
export async function fetchSpecialsFromDb(): Promise<{ specials: Special[]; schedules: SpecialSchedule[] }> {
    const [specialsRes, schedulesRes] = await Promise.all([
        supabase.from('specials').select('*').order('created_at', { ascending: false }),
        supabase.from('special_schedules').select('*').order('created_at', { ascending: false }),
    ]);
    if (specialsRes.error) throw specialsRes.error;
    if (schedulesRes.error) throw schedulesRes.error;
    return {
        specials:  (specialsRes.data  ?? []) as Special[],
        schedules: (schedulesRes.data ?? []) as SpecialSchedule[],
    };
}

// Creates or updates a special record. If editingId is provided, updates that row;
// otherwise inserts a new one. Returns the ID of the created/updated record.
export async function saveSpecialInDb(
    payload: Omit<Special, 'id' | 'sort_order' | 'created_at'>,
    editingId?: string
): Promise<string> {
    const { data, error } = editingId
        ? await supabase.from('specials').update(payload).eq('id', editingId).select('id').single()
        : await supabase.from('specials').insert(payload).select('id').single();
    if (error) throw error;
    return data.id;
}

// Permanently deletes a special by ID. Associated schedules should be removed
// first via deleteSchedulesForSpecialInDb to avoid orphaned schedule rows.
export async function deleteSpecialFromDb(id: string): Promise<void> {
    const { error } = await supabase.from('specials').delete().eq('id', id);
    if (error) throw error;
}

// Creates or updates the schedule for a special. Checks whether a schedule already
// exists for the given specialId — if so, updates it in place; otherwise inserts fresh.
// A special can only have one active schedule at a time.
export async function saveScheduleInDb(
    schedulePayload: object,
    specialId: string,
    existingSchedules: SpecialSchedule[]
): Promise<void> {
    const existing = existingSchedules.find(s => s.special_id === specialId);
    if (existing) {
        const { error } = await supabase.from('special_schedules').update(schedulePayload).eq('id', existing.id);
        if (error) throw error;
    } else {
        const { error } = await supabase.from('special_schedules').insert(schedulePayload);
        if (error) throw error;
    }
}

// Deletes all schedule records linked to a special. Called before deleting the special
// itself, or when the admin removes scheduling from an existing special.
export async function deleteSchedulesForSpecialInDb(specialId: string): Promise<void> {
    const { error } = await supabase.from('special_schedules').delete().eq('special_id', specialId);
    if (error) throw error;
}

// Fetches products for the special link target picker. Uses full-text search
// (via the product_inventory_view search_fts column) when the query meets the
// minimum character threshold; otherwise returns the first N products by name.
// Results are capped at SEARCH_RESULT_LIMIT to keep the picker responsive.
export async function fetchProductsFromDb(searchQuery = ''): Promise<ProductItem[]> {
    if (searchQuery.trim().length >= MIN_SEARCH_CHARS) {
        const { data, error } = await supabase
            .from('product_inventory_view')
            .select('id, name, image_url')
            .textSearch('search_fts', searchQuery.trim(), { type: 'websearch', config: 'simple' })
            .limit(SEARCH_RESULT_LIMIT);
        if (error) throw error;
        return (data ?? []) as ProductItem[];
    }
    const { data, error } = await supabase
        .from('product_inventory_view')
        .select('id, name, image_url')
        .order('name')
        .limit(SEARCH_RESULT_LIMIT);
    if (error) throw error;
    return (data ?? []) as ProductItem[];
}

// Fetches categories for the special link target picker, filtered by name
// with ilike when a search query is provided. Capped at SEARCH_RESULT_LIMIT.
export async function fetchCategoriesFromDb(searchQuery = ''): Promise<CategoryItem[]> {
    let q = supabase.from('categories').select('id, name').order('name').limit(SEARCH_RESULT_LIMIT);
    if (searchQuery.trim().length >= MIN_SEARCH_CHARS) {
        q = q.ilike('name', `%${searchQuery.trim()}%`);
    }
    const { data, error } = await q;
    if (error) throw error;
    return (data ?? []) as CategoryItem[];
}

// Lists images from the 'specials' folder in the product_images storage bucket,
// paginated by offset using IMAGE_PAGE_SIZE. Returns name, storage path, and
// public URL for each image. Used by the image picker in the specials editor.
export async function fetchBucketImagesFromDb(offset: number): Promise<StorageImageItem[]> {
    const { data, error } = await supabase.storage
        .from('product_images')
        .list('specials', { limit: IMAGE_PAGE_SIZE, offset, sortBy: { column: 'name', order: 'asc' } });
    if (error) throw error;
    return (data ?? [])
        .filter(f => !!f.name)
        .map(f => {
            const path = `specials/${f.name}`;
            const { data: pub } = supabase.storage.from('product_images').getPublicUrl(path);
            return { name: f.name, path, publicUrl: pub.publicUrl };
        });
}

// Uploads an image file to the 'specials' folder in the product_images bucket.
// Prefixes the filename with a timestamp to avoid collisions. Spaces in filenames
// are replaced with hyphens. Returns the public URL of the uploaded image.
export async function uploadSpecialImageToDb(file: File): Promise<string> {
    const cleanName = file.name.replace(/\s+/g, '-').toLowerCase();
    const filePath  = `specials/${Date.now()}-${cleanName}`;
    const { error } = await supabase.storage.from('product_images').upload(filePath, file, { upsert: false });
    if (error) throw error;
    const { data } = supabase.storage.from('product_images').getPublicUrl(filePath);
    return data.publicUrl;
}

// Formats an ISO timestamp into a human-readable date/time string in the
// business timezone (Africa/Johannesburg). Returns '—' for null/undefined.
export const formatDateTimeInBusinessTZ = (value?: string | null): string => {
    if (!value) return '—';
    return new Intl.DateTimeFormat('en-ZA', {
        dateStyle: 'medium', timeStyle: 'short', timeZone: BUSINESS_TIME_ZONE,
    }).format(new Date(value));
};

// Converts a datetime-local input value (which the browser treats as local time)
// into a UTC ISO string anchored to the business timezone (Africa/Johannesburg).
// Necessary because datetime-local inputs have no timezone awareness — without
// this conversion, a time entered as '09:00' in SAST would be stored as 09:00 UTC
// (two hours behind), causing specials to go live at the wrong time.
export const businessDatetimeLocalToISO = (value: string): string => {
    const [datePart, timePart] = value.split('T');
    if (!datePart || !timePart) return value;
    const [year, month, day] = datePart.split('-').map(Number);
    const [hour, minute]     = timePart.split(':').map(Number);
    const utcGuess  = new Date(Date.UTC(year, month - 1, day, hour, minute, 0));
    const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: BUSINESS_TIME_ZONE, year: 'numeric', month: '2-digit',
        day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    });
    const parts = formatter.formatToParts(utcGuess);
    const map   = Object.fromEntries(parts.map(p => [p.type, p.value]));
    const asBusinessUtc = Date.UTC(Number(map.year), Number(map.month) - 1, Number(map.day), Number(map.hour), Number(map.minute), Number(map.second));
    const offsetMs = asBusinessUtc - utcGuess.getTime();
    return new Date(utcGuess.getTime() - offsetMs).toISOString();
};